/**
 * parseParkedViewStatement — helper that pulls a view name + body text out of
 * the parked statement text. DSE2's `extractDdlStructure` does not yet expose
 * a structured view arm (DSE2 §3.D2 carries the planner but the extractor
 * lands in a follow-up DSE2 slice), so the inline `[Edit Body]` popover does
 * a lightweight lexical scan instead. This is intentionally permissive — it
 * accepts CREATE [OR REPLACE | OR ALTER] [TEMP|TEMPORARY] [MATERIALIZED] VIEW
 * (and per-engine modifiers) and stops at the first ` AS ` token following
 * the view name.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice D2.
 */
import type { QualifiedName } from '../../../sql-grammar/model';

export interface ParsedParkedView {
  readonly name: QualifiedName;
  readonly bodyText: string;
  readonly materialized: boolean;
  /** Column-alias list captured from `CREATE VIEW <name> (col1, col2) AS …`.
   *  Empty when the parked statement omits the parenthesized list. Each entry
   *  is the verbatim source token (preserves user quoting / casing). */
  readonly columns: readonly string[];
}

const QUALIFIED_NAME_RE = /^([A-Za-z_][A-Za-z0-9_]*|"[^"]+"|`[^`]+`|\[[^\]]+\])(?:\s*\.\s*([A-Za-z_][A-Za-z0-9_]*|"[^"]+"|`[^`]+`|\[[^\]]+\]))?/;

/** Strip surrounding quote / backtick / bracket pairs and return the bare
 *  identifier text (preserving case). */
function unquoteIdent(raw: string): { name: string; quoted: boolean } {
  if (raw.length >= 2) {
    const first = raw.charAt(0);
    const last = raw.charAt(raw.length - 1);
    if ((first === '"' && last === '"') || (first === '`' && last === '`')) {
      return { name: raw.slice(1, -1), quoted: true };
    }
    if (first === '[' && last === ']') {
      return { name: raw.slice(1, -1), quoted: true };
    }
  }
  return { name: raw, quoted: false };
}

function buildQualifiedName(rawHead: string, rawTail: string | undefined): QualifiedName {
  if (rawTail !== undefined) {
    const head = unquoteIdent(rawHead);
    const tail = unquoteIdent(rawTail);
    return {
      schema: head.name,
      name: tail.name,
      raw: `${rawHead}.${rawTail}`,
      quoted: head.quoted || tail.quoted,
      foldedName: tail.name.toLowerCase(),
      foldedSchema: head.name.toLowerCase(),
    };
  }
  const ident = unquoteIdent(rawHead);
  return {
    name: ident.name,
    raw: rawHead,
    quoted: ident.quoted,
    foldedName: ident.name.toLowerCase(),
  };
}

/** Find the offset of the next ` AS ` token (case-insensitive, surrounded by
 *  whitespace). Returns `-1` when none is found. */
function findAsToken(text: string, start: number): number {
  for (let i = start; i + 4 <= text.length; i++) {
    const a = text.charCodeAt(i);
    const isLeftBoundary = i === 0 || isWs(text.charCodeAt(i - 1));
    if (!isLeftBoundary) continue;
    if ((a === 65 || a === 97) && (text.charCodeAt(i + 1) === 83 || text.charCodeAt(i + 1) === 115)) {
      const after = text.charCodeAt(i + 2);
      if (isWs(after)) return i;
    }
  }
  return -1;
}

function isWs(code: number): boolean {
  return code === 32 || code === 9 || code === 10 || code === 13;
}

/** Parse a CREATE [OR REPLACE | OR ALTER] [TEMP|TEMPORARY] [MATERIALIZED]
 *  VIEW <name> [(...) | WITH ...] AS <body> statement. Returns `null` when
 *  the input does not look like a CREATE/ALTER VIEW statement.
 *  Tolerates leading whitespace + comments (caller may pre-strip if needed). */
export function parseParkedViewStatement(stmtText: string): ParsedParkedView | null {
  const trimmed = stmtText.trim();
  const upper = trimmed.toUpperCase();
  let materialized = false;

  let cursor = 0;
  if (upper.startsWith('CREATE')) {
    cursor = 6;
  } else if (upper.startsWith('ALTER')) {
    cursor = 5;
  } else {
    return null;
  }

  cursor = skipWs(trimmed, cursor);

  // Optional modifiers: OR REPLACE / OR ALTER / TEMP / TEMPORARY / MATERIALIZED
  // / EDITIONABLE / NONEDITIONABLE / FORCE / DEFINER = … / ALGORITHM = … /
  // SQL SECURITY {DEFINER|INVOKER}. We only need to recognise enough to find
  // the VIEW keyword and the qualified name.
  while (cursor < trimmed.length) {
    const word = peekWord(trimmed, cursor);
    if (word === null) break;
    const wordUpper = word.toUpperCase();
    if (wordUpper === 'VIEW') break;
    if (wordUpper === 'MATERIALIZED') {
      materialized = true;
      cursor = skipWs(trimmed, cursor + word.length);
      continue;
    }
    if (
      wordUpper === 'OR' || wordUpper === 'REPLACE' || wordUpper === 'ALTER'
      || wordUpper === 'TEMP' || wordUpper === 'TEMPORARY'
      || wordUpper === 'EDITIONABLE' || wordUpper === 'NONEDITIONABLE'
      || wordUpper === 'FORCE' || wordUpper === 'NO' || wordUpper === 'ALGORITHM'
      || wordUpper === 'SQL' || wordUpper === 'SECURITY' || wordUpper === 'DEFINER'
      || wordUpper === 'INVOKER' || wordUpper === 'UNDEFINED' || wordUpper === 'MERGE'
      || wordUpper === 'TEMPTABLE'
    ) {
      cursor = skipWs(trimmed, cursor + word.length);
      // Consume `= <value>` if present (mysql DEFINER / ALGORITHM).
      if (trimmed.charAt(cursor) === '=') {
        cursor = skipWs(trimmed, cursor + 1);
        const value = peekWord(trimmed, cursor) ?? '';
        cursor = skipWs(trimmed, cursor + value.length);
      }
      continue;
    }
    break;
  }

  // VIEW keyword
  const viewWord = peekWord(trimmed, cursor);
  if (viewWord === null || viewWord.toUpperCase() !== 'VIEW') return null;
  cursor = skipWs(trimmed, cursor + viewWord.length);

  // Optional `IF NOT EXISTS` between `VIEW` and the qualified name (postgres /
  // mysql / sqlite `CREATE [MATERIALIZED] VIEW IF NOT EXISTS …`). Skip the full
  // triple only when all three keywords are present so a view literally named
  // `if` is never swallowed.
  if (peekWord(trimmed, cursor)?.toUpperCase() === 'IF') {
    const afterIf = skipWs(trimmed, cursor + 2);
    const notWord = peekWord(trimmed, afterIf);
    if (notWord?.toUpperCase() === 'NOT') {
      const afterNot = skipWs(trimmed, afterIf + notWord.length);
      const existsWord = peekWord(trimmed, afterNot);
      if (existsWord?.toUpperCase() === 'EXISTS') {
        cursor = skipWs(trimmed, afterNot + existsWord.length);
      }
    }
  }

  // Qualified name
  const remaining = trimmed.slice(cursor);
  const match = QUALIFIED_NAME_RE.exec(remaining);
  if (match === null) return null;
  const name = buildQualifiedName(match[1]!, match[2]);
  cursor += match[0].length;

  // Optional column-alias list `(col1, col2, …)` between the view name and
  // the WITH / AS keywords. Only consume it when the next non-whitespace char
  // is `(` AND a balanced close paren is found before any `AS`. Per-engine
  // WITH (option = …) clauses also use parens but always come after a `WITH`
  // keyword, so we anchor the column list directly after the name.
  cursor = skipWs(trimmed, cursor);
  const columns: string[] = trimmed.charAt(cursor) === '('
    ? consumeColumnAliasList(trimmed, cursor)
    : [];
  if (columns.length > 0) {
    // Advance the cursor past the closed paren that produced the columns list.
    cursor = findMatchingClose(trimmed, cursor) + 1;
  }

  // Find ` AS ` token (case-insensitive, whitespace-bounded). Skip per-engine
  // WITH clauses by scanning forward.
  const asIdx = findAsToken(trimmed, cursor);
  if (asIdx === -1) return null;
  const bodyStart = asIdx + 3;
  const bodyText = trimmed.slice(bodyStart).replace(/;\s*$/, '').trim();
  return { name, bodyText, materialized, columns };
}

function findMatchingClose(text: string, openIdx: number): number {
  let depth = 0;
  for (let i = openIdx; i < text.length; i++) {
    const c = text.charAt(i);
    if (c === '(') depth++;
    else if (c === ')') {
      depth--;
      if (depth === 0) return i;
    }
  }
  return -1;
}

function consumeColumnAliasList(text: string, openIdx: number): string[] {
  const closeIdx = findMatchingClose(text, openIdx);
  if (closeIdx === -1) return [];
  const inner = text.slice(openIdx + 1, closeIdx);
  return inner
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

function skipWs(text: string, start: number): number {
  let i = start;
  while (i < text.length) {
    const c = text.charCodeAt(i);
    if (c === 32 || c === 9 || c === 10 || c === 13) {
      i++;
      continue;
    }
    if (c === 47 && text.charCodeAt(i + 1) === 47) {
      while (i < text.length && text.charCodeAt(i) !== 10) i++;
      continue;
    }
    if (c === 47 && text.charCodeAt(i + 1) === 42) {
      i += 2;
      while (i + 1 < text.length && !(text.charCodeAt(i) === 42 && text.charCodeAt(i + 1) === 47)) i++;
      i += 2;
      continue;
    }
    if (c === 45 && text.charCodeAt(i + 1) === 45) {
      while (i < text.length && text.charCodeAt(i) !== 10) i++;
      continue;
    }
    break;
  }
  return i;
}

function peekWord(text: string, start: number): string | null {
  if (start >= text.length) return null;
  const head = text.charCodeAt(start);
  const isAlpha = (head >= 65 && head <= 90) || (head >= 97 && head <= 122) || head === 95;
  if (!isAlpha) return null;
  let i = start;
  while (i < text.length) {
    const c = text.charCodeAt(i);
    const ok = (c >= 65 && c <= 90) || (c >= 97 && c <= 122) || (c >= 48 && c <= 57) || c === 95;
    if (!ok) break;
    i++;
  }
  return text.slice(start, i);
}
